<template>
  <div class="screen-size">
    <v-card class="d-flex pa-1 bg" outlined>
      <div class="mr-auto d-flex">
        <system-linked-combo v-model="selectedSystem" :hideStack="true" @input="changeSystem" />
        <!-- eslint-disable-next-line prettier/prettier -->
        <v-select
          v-model="selectedDataSetTime" :items="dataSets" item-text="label" item-value="time"
          :menu-props="{ offsetY: true }" solo dense hide-details class="mr-1"
          style="width: 300px"
        />
        <!-- eslint-disable-next-line prettier/prettier -->
        <v-select
          v-model="selectedMandt" :items="mandts" item-text="label" item-value="clientNo"
          :menu-props="{ offsetY: true }" solo dense hide-details class="pr-2"
          style="width: 160px"
        />
      </div>
      <div>
        <v-btn class="mr-2" x-small color="primary" :disabled="disableAnalyzeOpen" @click="openAnalyzeConfirm()">
          <v-icon small left>mdi-poll</v-icon>
          {{ this.$t('common.analyze') }}
        </v-btn>
        <v-btn x-small :disabled="disableStop" @click="stopAnalyzing">
          <v-icon small left>mdi-stop-circle</v-icon>
          {{ this.$t('sod.stop') }}
        </v-btn>
        <v-btn x-small :disabled="disableDelete" @click="deleteAnalyzed">
          <v-icon small left>mdi-trash-can-outline</v-icon>
          {{ this.$t('sod.delete') }}
        </v-btn>
        <span class="mx-3">|</span>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" @click="paging = { page: 1, rowsPerPage: paging.rowsPerPage }" color="primary" x-small>
              <v-icon small>mdi-magnify</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('common.retrieve') }}</span>
        </v-tooltip>
      </div>
    </v-card>
    <!-- eslint-disable-next-line prettier/prettier -->
    <v-data-table
      v-model="selectedHistories" :headers="headers" :items="histories" item-key="analYmdhms"
      :options.sync="options" :items-per-page="paging.rowsPerPage" :loading="loading" :height="height"
      class="elevation-1 content" must-sort show-select single-select fixed-header dense hide-default-footer
    >
      <template v-slot:item.rtrckYmdhms="{ item }"> {{ item.rtrckYmdhms | format('yyyy-MM-dd') }} </template>
      <template v-slot:item.analType="{ item }"> {{ $t(`sod.anal.type.${item.analType}`) }} </template>
      <template v-slot:item.analYmdhms="{ item }"> {{ item.analYmdhms | format('yyyy-MM-dd HH:mm:ss') }} </template>
      <template v-slot:item.execType="{ item }"> {{ $t(`sod.exec.type.${item.execType}`) }} </template>
      <template v-slot:item.execStatus="{ item }"> {{ $t(`cmmn.exec.status.${item.execStatus}`) }} </template>
      <template v-slot:item.execRate="{ item }"> {{ `${item.execRate} %` }} </template>
      <template v-slot:item.analDesc="{ item }"> {{ `${item.exMsg == '' ? item.analDesc : item.exMsg}` }} </template>
      <template v-slot:footer>
        <pagination :paging.sync="paging" :length="length" :disabled="loading"></pagination>
      </template>
    </v-data-table>
    <!-- 수집 작업 시작 확인 창  -->
    <v-dialog v-model="isShowForm" persistent max-width="600px">
      <v-card :class="classContents">
        <v-card-title>
          <h3>{{ $t('common.analyze') }}</h3>
          <v-spacer></v-spacer>
          <v-btn icon x-small @click="isShowForm = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row class="pt-5">
            <!-- eslint-disable-next-line prettier/prettier -->
            <v-col cols="3"><p>{{ this.$t('sod.anal.type') }}</p></v-col>
            <v-col cols="3" v-for="(item, idx) in analTypes" :key="idx">
              <v-checkbox v-model="analType" :label="item.label" :value="item.value" hide-details dense />
            </v-col>
            <!-- eslint-disable-next-line prettier/prettier -->
            <v-col cols="3" class="pt-2"><p>{{ this.$t('sod.target.role') }}</p></v-col>
            <v-col cols="4" class="pt-2">
              <v-checkbox v-model="onlyGenerated" :label="$t('sod.only.generated')" hide-details dense />
            </v-col>
            <v-col cols="4" class="pt-2">
              <v-checkbox v-model="onlyCompared" :label="$t('sod.only.compared')" hide-details dense />
            </v-col>
            <v-col cols="1"></v-col>
            <!-- eslint-disable-next-line prettier/prettier -->
            <v-col cols="3" class="pt-2"><p>{{ this.$t('sod.anal.reason') }}</p></v-col>
            <v-col cols="9" class="pt-2">
              <v-textarea v-model="analDesc" rows="1" auto-grow outlined :hide-details="true" style="width: 100%"></v-textarea>
            </v-col>
            <v-col cols="12" class="pt-5">
              <p style="font-size: 1.5em; line-height: 1.5">{{ this.$t('sod.confirm.analyze') }}</p>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn :disabled="disableAnalyze" small color="primary" @click="doAnalyze()">{{ $t('common.analyze') }}</v-btn>
          <v-btn small color="primary" @click="closeAnalyzeConfirm">{{ $t('common.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { isEmpty } from 'lodash';
import { mapGetters } from 'vuex';

import { api } from '@/api/common/instances';
import SystemLinkedCombo from '@/components/SystemLinkedCombo.vue';

export default {
  components: {
    SystemLinkedCombo,
  },
  data() {
    return {
      /* 분석대상 조건 */
      selectedSystem: { stack: 'ABAP', system: '' },
      selectedDataSetTime: 0,
      selectedMandt: '',
      dataSets: [],
      mandts: [],
      /* data-table */
      histories: [],
      options: {},
      length: 0,
      paging: { page: 1, rowsPerPage: 50 },
      /* Miscellaneous */
      loading: false,
      progress: false,
      height: 530,
      selectedHistories: [],
      /* 확인창 */
      isShowForm: false,
      analType: 'A',
      analDesc: '',
      onlyGenerated: true,
      onlyCompared: true,
    };
  },
  computed: {
    ...mapGetters('common/system', ['getDescendantsById']),
    classContents() {
      return {
        'contents-background-dark': this.$vuetify.theme.isDark,
      };
    },
    disableAnalyzeOpen() {
      if (this.selectedDataSetTime == 0) return true;
      return false;
    },
    disableStop() {
      if (this.selectedHistories.length == 0) return true;
      let elm = this.selectedHistories[0];
      return elm.execType != '1' || (elm.execType === '1' && elm.execStatus != '0');
    },
    disableDelete() {
      if (this.selectedHistories.length == 0) return true;
      return this.selectedHistories[0].execStatus === '0';
    },
    disableAnalyze() {
      if (isEmpty(this.analType)) return true;
      if (isEmpty(this.analDesc)) return true;
      return false;
    },
    headers() {
      return [
        { text: this.$t('sod.rtrck.datetime'), value: 'rtrckYmdhms', sortable: false, width: '90px', align: 'center' },
        { text: this.$t('sod.anal.mandt'), value: 'analMandt', sortable: false, width: '80px', align: 'center' },
        { text: this.$t('sod.anal.type'), value: 'analType', sortable: false, width: '90px', align: 'center' },
        { text: this.$t('sod.anal.datetime'), value: 'analYmdhms', sortable: false, width: '140px', align: 'center' },
        { text: this.$t('sod.exec.type'), value: 'execType', sortable: false, width: '60px', align: 'center' },
        { text: this.$t('sod.exec.status'), value: 'execStatus', sortable: false, width: '80px', align: 'center' },
        { text: this.$t('sod.exec.rate'), value: 'execRate', sortable: false, width: '80px', align: 'end' },
        { text: this.$t('sod.anal.desc'), value: 'analDesc', sortable: false },
      ];
    },
    analTypes() {
      return [
        { label: this.$t('sod.anal.type.A'), value: 'A' },
        { label: this.$t('sod.anal.type.S'), value: 'S' },
        { label: this.$t('sod.anal.type.R'), value: 'R' },
      ];
    },
  },
  watch: {
    /**
     * 분석 대상이 되는 Role-tracker 데이터 셋의 선택이 바뀌면
     * 선택된 데이터 셋에 포함된 Client No. 목록을 리셋한다.
     */
    selectedDataSetTime: {
      handler() {
        this.getRtrckMandts();
      },
    },
    paging: {
      deep: true,
      handler() {
        this.fetchHistories();
      },
    },
  },
  methods: {
    async changeSystem() {
      await this.getRtrckDataSets();
      await this.fetchHistories();
    },
    /**
     * 상단 시스템 선택기에서 선택된 SAP 시스템의
     * Role-tracker 수집 데이터 셋의 목록을 읽어온다.
     */
    async getRtrckDataSets() {
      this.loading = true;
      const { data } = await api.get('/sod/analyze/data-sets', {
        params: {
          sstmId: this.selectedSystem.system,
        },
      });
      if (isEmpty(data)) {
        this.loading = false;
        this.dataSets = [];
        this.selectedDataSetTime = 0;
        this.mandts = [];
        this.selectedMandt = '';
        this.$dialog.alert(this.$t('rtrck.msg.no.dataset'));
        return;
      }
      this.dataSets = data.map(elm => {
        return { label: `${this.$t('rtrck.data.set')}  (  ${format(elm, 'yyyy-MM-dd')}  )`, time: elm };
      });
      this.selectedDataSetTime = this.dataSets[0].time;
      this.loading = false;
    },
    /**
     * 상단 시스템 선택기에서 선택된 SAP 시스템의
     * Role-tracker 수집 데이터 셋이 있어서 선택된 데이터 셋에
     * 포함된 Client No. 목록을 읽어온다.
     */
    async getRtrckMandts() {
      this.loading = true;
      const { data } = await api.get('/sod/analyze/mandts', {
        params: {
          sstmId: this.selectedSystem.system,
          svrYmdhms: this.selectedDataSetTime,
        },
      });
      this.mandts = data.map(elm => {
        return { label: `${this.$t('sod.anal.mandt')}  (  ${elm}  )`, clientNo: elm };
      });
      const sapInstance = this.getDescendantsById(this.selectedSystem.system);
      this.selectedMandt = sapInstance[0].jco.clientNumber;
      this.loading = false;
    },
    /**
     * 조회조건 또는 분석 이력 데이터 변경 시 분석 이력 조회.
     * 단, 데이터 셋이나 Client No.에 상관없이 시스템에 속한 이력을 모두 조회한다.
     */
    async fetchHistories() {
      this.loading = true;
      // eslint-disable-next-line prettier/prettier
      const { data: { items, pagination } } = await api.get('/sod/analyze/histories', {
        params: {
          sstmId: this.selectedSystem.system,
          page: this.paging.page,
          limit: this.paging.rowsPerPage,
        },
      });
      this.histories = items;
      this.selectedHistories = []; // 선택 초기화
      this.length = pagination.totalCount;
      this.loading = false;
    },
    /**
     * 실행유형(분석 or 삭제)별로 기존에 실행 중인 것이 있는 지 여부 체크
     */
    async checkExecStatus(execType) {
      const { data } = await api.get('/sod/analyze/is-running', {
        params: {
          sstmId: this.selectedSystem.system,
          execType,
        },
      });
      return data;
    },
    /**
     * 분석 중인 것이 있으면 메시지 표시하고 리턴.
     * 그렇지 않으면 분석 대화 창 열기.
     */
    async openAnalyzeConfirm() {
      const isRunning = await this.checkExecStatus('1'); // '1': UPDATE
      if (isRunning == true) {
        this.$snackbar(this.$t('sod.msg.is.running')); // 이미 분석 중입니다.
        return;
      }
      this.isShowForm = true;
    },
    /**
     * 분석 시작.
     */
    async doAnalyze() {
      const params = {
        rtrckYmdhms: this.selectedDataSetTime,
        sstmId: this.selectedSystem.system,
        mandt: this.selectedMandt,
        analType: this.analType,
        onlyGenerated: this.onlyGenerated ? 1 : 0,
        onlyCompared: this.onlyCompared ? 1 : 0,
        analDesc: this.analDesc,
      };
      const { status } = await api.post('/sod/analyze/analyze', params);
      if (status == 200) {
        this.closeAnalyzeConfirm();
        setTimeout(() => {
          this.fetchHistories();
        }, 500);
      }
    },
    closeAnalyzeConfirm() {
      this.isShowForm = false;
      this.analType = 'A';
      this.onlyGenerated = true;
      this.onlyCompared = true;
      this.analDesc = '';
    },
    /**
     * 선택된 현재 분석 중인 작업 중단
     */
    async stopAnalyzing() {
      let confirmRslt = await this.$dialog.confirm(this.$t('sod.confirm.stop'));
      if (confirmRslt) {
        const params = {
          analYmdhms: this.selectedHistories[0].analYmdhms,
        };
        const { status } = await api.post('/sod/analyze/stop', params);
        if (status == 200) {
          this.$snackbar(this.$t('sod.msg.stopped'));
          this.fetchHistories();
        }
      }
    },
    /**
     * 선택된 분석 내역 삭제
     */
    async deleteAnalyzed() {
      const isDeleting = this.checkExecStatus('2'); // Delete
      if (isDeleting == true) {
        this.$snackbar(this.$t('sod.msg.is.erasing')); // 이미 삭제 중입니다.
        return;
      }
      let confirmRslt = await this.$dialog.confirm(this.$t('sod.confirm.delete'));
      if (confirmRslt) {
        const params = {
          analYmdhms: this.selectedHistories[0].analYmdhms,
        };
        const { status } = await api.post('/sod/analyze/delete', params);
        if (status == 200) {
          this.fetchHistories();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.screen-size {
  width: 1200px;
  height: 600px;
}
.v-input--selection-controls {
  margin: 0 0 0 3px;
  padding: 0;
}
.v-data-table {
  border-radius: 0px;
}
</style>
